import React, { useState } from 'react'
import { Form, Input, Typography } from 'antd'
import type { PotentialProfileSuggestionMutation } from 'apollo-gql'
import { useTranslation } from 'lib/i18n'
import trackPotentialProfileSuggested from 'lib/tracking/avo/trackPotentialProfileSuggested'

import Heading from 'components/Heading/Heading'
import { Button } from 'components/UI/Button/Button'
import usePotentialProfileSuggestion from 'hooks/usePotentialProfileSuggestion'

import styles from './PotentialProfileSuggestionForm.module.css'

const { Text } = Typography
const { TextArea } = Input

const percent = 10

type PotentialProfileSuggestionFormProps = {
  couponCode?: string
  initialValues: {
    name: string
  }
}

const PotentialProfileSuggestionForm: React.VFC<
  PotentialProfileSuggestionFormProps
> = ({ couponCode, initialValues }) => {
  const { t } = useTranslation()

  const [done, setDone] = useState(false)
  const [suggest, { loading }] = usePotentialProfileSuggestion({
    onCompleted: (
      result: PotentialProfileSuggestionMutation['market']['suggestPotentialProfile'],
    ) => {
      setDone(true)
      trackPotentialProfileSuggested({
        result,
      })
    },
  })

  return (
    <Form
      layout="vertical"
      onFinish={(input) => {
        suggest({
          name: input.name,
          email: input.email,
          description: input.description,
          profilePicture: input.profilePicture,
        })
      }}
      initialValues={initialValues}
    >
      {done && (
        <div>
          <div className={styles.text}>
            {t(
              'PotentialProfileSuggestionForm.thanksText',
              'Thanks for submitting. You will hear from us soon!',
            )}
          </div>
          {couponCode && (
            <div className={styles.couponCode}>
              {t(
                'PotentialProfileSuggestionForm.couponCodeText',
                'Use this code to get {{percent}}% on your next order:',
                {
                  percent,
                },
              )}
              <div>
                <Text copyable>{couponCode}</Text>
              </div>
            </div>
          )}
        </div>
      )}

      {!done && (
        <div className={styles.fields}>
          <Heading level="h2">
            <h1>
              {t(
                'PotentialProfileSuggestionForm.heading',
                'Who would you like to see on memmo?',
              )}
            </h1>
          </Heading>

          <Form.Item
            name="name"
            label={t(
              'PotentialProfileSuggestionForm.nameLabel',
              'Name of talent',
            )}
            rules={[
              {
                required: true,
                message: t(
                  'PotentialProfileSuggestionForm.nameRequired',
                  'Name is required',
                ),
              },
            ]}
          >
            <Input
              size="large"
              disabled={loading}
              placeholder={t(
                'PotentialProfileSuggestionForm.namePlaceholder',
                'Name of talent',
              )}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label={t(
              'PotentialProfileSuggestionForm.descriptionLabel',
              'What is the talent famous for?',
            )}
          >
            <TextArea disabled={loading} />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('PotentialProfileSuggestionForm.emailLabel', 'Your email')}
            rules={[
              {
                required: true,
                message: t(
                  'PotentialProfileSuggestionForm.emailRequired',
                  'E-mail is required',
                ),
              },
            ]}
          >
            <Input
              type="email"
              size="large"
              disabled={loading}
              placeholder={t(
                'PotentialProfileSuggestionForm.emailPlaceholder',
                'Your email',
              )}
            />
          </Form.Item>
          <Button
            htmlType="submit"
            loading={loading}
            size="large"
            type="primary"
          >
            {t('PotentialProfileSuggestionForm.cta', 'Send')}
          </Button>
        </div>
      )}
    </Form>
  )
}

export default PotentialProfileSuggestionForm
